import { useGqtyClient, useTeam } from '@vrplatform/data';
import { showLoadingNotification } from '@vrplatform/theme';
import {
  updateErrorNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { hasValue, waitFor } from '@vrplatform/utils';
import { ownerStatementCsvQuery } from '@vrplatform/ui-common';
import { useState } from 'react';
import type { LegacySubmitConfirmModalType } from '../../../../components/legacy/legacy-confirm-modal';
import { createZipBlob, getCsvUrl } from '../../../../utils';

export const useStatementCsvMultipleExport = (withNotification?: boolean) => {
  const [loading, setLoading] = useState(false);
  const client = useGqtyClient();
  const [{ id: teamId }] = useTeam();

  const generateCsv = async (id: string) => {
    return await ownerStatementCsvQuery(client, {
      groupedBy: 'groupByReservation',
      ownerId: undefined,
      statementIds: [id],
      teamId,
    });
  };

  const handleExportCsvs: LegacySubmitConfirmModalType<string[]> = async (
    statementIds
  ) => {
    setLoading(true);
    const notifyId = 'export-statement-csv';

    if (withNotification) showLoadingNotification({ id: notifyId });

    try {
      await waitFor(100);

      if ((statementIds || []).length < 1) {
        throw Error('Missing selected statements.');
      }

      const data = (
        await Promise.all(
          (statementIds || []).map(async (id) => {
            const res = await generateCsv(id).then(({ rows, listingName }) => {
              const url = getCsvUrl(rows);
              return { url, filename: listingName };
            });
            return res;
          })
        )
      ).filter(hasValue);

      await zipUrls(data, 'csv');

      if (withNotification) {
        updateSuccessNotification({
          id: notifyId,
          title: 'Statement CSVs',
          message: 'Statements were successfully exported!',
        });
      }

      setLoading(false);
      return {
        message: 'Statements were successfully exported!',
        isSuccess: true,
      };
    } catch (err) {
      console.error(err);

      if (withNotification) {
        updateErrorNotification({
          id: notifyId,
          title: 'Error: Statement CSVs',
          message: 'Failed to export statements. Please try again.',
        });
      }
      setLoading(false);
      return {
        message: (err as any).message || 'Export failed. Please try again.',
        isSuccess: false,
      };
    }
  };

  return {
    handleExportCsvs,
    loading,
  };
};

export async function zipUrls(
  data: { url: string; filename: string | undefined }[],
  fileType: 'csv' | 'pdf'
) {
  const urls = data.map((d) => d.url);

  const files = await Promise.all(
    urls.map(async (url) => fetch(url).then((res) => res.blob()))
  );

  const zipblob = await createZipBlob(
    files.map((file, index) => {
      const defaultFileName = `statement_${index}`;
      const filename = data[index].filename || defaultFileName;

      return {
        filename: `${filename || defaultFileName}.${fileType}`,
        blob: file,
      };
    })
  );

  const elem = window.document.createElement('a');
  elem.href = window.URL.createObjectURL(zipblob);
  elem.download = 'compressed.zip';
  elem.click();
}

export async function zipBlobs(
  files: { filename: string; blob: Blob }[],
  fileType: 'csv' | 'pdf'
) {
  const zipblob = await createZipBlob(
    files.map((file, index) => {
      const defaultFileName = `statement_${index}`;
      const filename = file.filename;

      return {
        filename: `${filename || defaultFileName}.${fileType}`,
        blob: file.blob,
      };
    })
  );

  const elem = window.document.createElement('a');
  elem.href = window.URL.createObjectURL(zipblob);
  elem.download = 'compressed.zip';
  elem.click();
}
